import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { useNavigate, useOutletContext, useParams, useLocation } from 'react-router-dom';
import { useSessionDetailPlayer } from '../FloatingPlayer';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { toast, Bounce } from 'react-toastify';
import CustomButton from '../OnboardingComponents/CustomButton';
import CustomIconButton from '../OnboardingComponents/CustomIconButton';
import EmptyState from '../Common/EmptyState';
import TruncatedText from '../Common/TruncatedText';
import DeleteConfirmation from '../Common/DeleteConfirmation';
import Pagination from '../Listing/Pagination';
import SearchInput from '../Listing/SearchInput';
import FilterDropdown from '../Listing/FilterDropdown';
import { AvatarGroup } from './AvatarGroup';
import {
  JoinShadowSessionModal,
  ModalActions,
  NoteField,
  SelectedProject,
  StartShadowSessionModal,
  StartSoloSessionModal,
  SelectedCity,
  WORKING_NOTE_MAX_LENGTH,
  getWorkingNoteError,
} from '../Modals/SessionModals';
import { ICONS } from '../../constants/icons';
import { get, del } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { getUserAvatarUrl } from '../../helpers/userAvatar';
import { getActiveSessionSnapshot } from '../../helpers/activeSession';
import { formatWorkedHours, formatSessionVisibilityLabel, getSessionVisibilityClassKey } from '../../helpers/sessionDetail';
import {
  createProjectSession,
  getProjectCollaborators,
  joinProjectSession,
} from '../../helpers/session';
import { projectTabReturnTo } from '../../helpers/sessionReturnTo';import avatar1 from '../../assets/images/users/avatar-1.jpg';
import avatar2 from '../../assets/images/users/avatar-2.jpg';
import avatar3 from '../../assets/images/users/avatar-3.jpg';
import avatar4 from '../../assets/images/users/avatar-4.jpg';
import avatar5 from '../../assets/images/users/avatar-5.jpg';
import avatar6 from '../../assets/images/users/avatar-6.jpg';

const PAGE_SIZE = 10;
const SKELETON_ROWS = 8;
const TABLE_COLUMNS = 7;
const SEARCH_DEBOUNCE_MS = 300;

const SESSION_TYPE_OPTIONS = [
  { value: 'solo', label: 'Solo' },
  { value: 'shadow', label: 'Shadow' },
];

const SESSION_SORT_OPTIONS = [
  { value: 'title', label: 'Title' },
  { value: 'created_at', label: 'Created At' },
  { value: 'updated_at', label: 'Updated At' },
  { value: 'started_at', label: 'Started At' },
  { value: 'visibility_type', label: 'Visibility' },
  { value: 'type', label: 'Type' },
];

const DEFAULT_SESSION_SORT_BY = 'created_at';
const DEFAULT_SESSION_SORT_DIRECTION = 'desc';

const SESSION_COLLABORATORS = [
  {
    id: 'mark-carlo',
    name: 'Mark Carlo',
    role: 'Guitarist',
    avatar: avatar1,
    status: 'In Session',
  },
  {
    id: 'sophia-lee',
    name: 'Sophia Lee',
    role: 'Photographer',
    avatar: avatar2,
    status: 'Paused',
  },
  {
    id: 'angela-karl',
    name: 'Angela Karl',
    role: 'Guitarist',
    avatar: avatar6,
    status: 'Invited',
  },
  {
    id: 'amira-patel',
    name: 'Amira Patel',
    role: 'Musician',
    avatar: avatar4,
    status: 'In Session',
  },
  {
    id: 'ethan-wright',
    name: 'Ethan Wright',
    role: 'Manager',
    avatar: avatar3,
    status: 'Exited',
  },
  {
    id: 'liam-jones',
    name: 'Liam Jones',
    role: 'Designer',
    avatar: avatar5,
    status: 'Invited',
  },
];

export const DUMMY_SESSIONS = [
  {
    id: 'session-1',
    type: 'Shadow Session',
    visibility: 'Private',
    note: 'Played Guitar for 2 hours',
    collabIndices: [0, 1],
    logged: '7.5hrs',
    endAt: 'June 12, 2025, 3:45 PM',
  },
  {
    id: 'session-2',
    type: 'Solo Session',
    visibility: '---',
    note: 'Played Guitar for 2 hours',
    collabIndices: [0],
    logged: '6.75hrs',
    endAt: 'June 13, 2025, 11:20 AM',
  },
  {
    id: 'session-3',
    type: 'Shadow Session',
    visibility: '---',
    note: 'Played Guitar for 2 hours',
    collabIndices: [1, 2],
    logged: '5.25hrs',
    endAt: 'June 14, 2025, 9:05 AM',
  },
  {
    id: 'session-4',
    type: 'Solo Session',
    visibility: '---',
    note: 'Played Guitar for 2 hours',
    collabIndices: [0],
    logged: '7.25hrs',
    endAt: 'June 15, 2025, 1:30 PM',
  },
  {
    id: 'session-5',
    type: 'Shadow Session',
    visibility: 'Private',
    note: 'Played Guitar for 2 hours',
    collabIndices: [0, 1],
    logged: '4.5hrs',
    endAt: 'June 16, 2025, 4:10 PM',
  },
  {
    id: 'session-6',
    type: 'Solo Session',
    visibility: '---',
    note: 'Played Guitar for 2 hours',
    collabIndices: [2],
    logged: '6.5hrs',
    endAt: 'June 17, 2025, 8:55 PM',
  },
  {
    id: 'session-7',
    type: 'Shadow Session',
    visibility: 'Private',
    note: 'Played Guitar for 2 hours',
    collabIndices: [0, 1],
    logged: '7.5hrs',
    endAt: 'June 12, 2025, 3:45 PM',
  },
  {
    id: 'session-8',
    type: 'Solo Session',
    visibility: '---',
    note: 'Played Guitar for 2 hours',
    collabIndices: [3],
    logged: '6.75hrs',
    endAt: 'June 13, 2025, 11:20 AM',
  },
  {
    id: 'session-9',
    type: 'Shadow Session',
    visibility: 'Public',
    note: 'Played Guitar for 2 hours',
    collabIndices: [4, 5],
    logged: '5.25hrs',
    endAt: 'June 14, 2025, 9:05 AM',
  },
  {
    id: 'session-10',
    type: 'Solo Session',
    visibility: '---',
    note: 'Played Guitar for 2 hours',
    collabIndices: [0],
    logged: '7.25hrs',
    endAt: 'June 15, 2025, 1:30 PM',
  },
  {
    id: 'session-11',
    type: 'Shadow Session',
    visibility: 'Public',
    note: 'Played Guitar for 2 hours',
    collabIndices: [1, 2],
    logged: '4.5hrs',
    endAt: 'June 16, 2025, 4:10 PM',
  },
  {
    id: 'session-12',
    type: 'Solo Session',
    visibility: '---',
    note: 'Played Guitar for 2 hours',
    collabIndices: [5],
    logged: '6.5hrs',
    endAt: 'June 17, 2025, 8:55 PM',
  },
  {
    id: 'session-13',
    type: 'Shadow Session',
    visibility: 'Private',
    note: 'Played Guitar for 2 hours',
    collabIndices: [0, 1],
    logged: '7.5hrs',
    endAt: 'June 18, 2025, 6:40 PM',
  },
  {
    id: 'session-14',
    type: 'Solo Session',
    visibility: '---',
    note: 'Played Guitar for 2 hours',
    collabIndices: [0],
    logged: '3.75hrs',
    endAt: 'June 19, 2025, 10:15 AM',
  },
].map(({ collabIndices, ...rest }) => ({
  ...rest,
  collaborators: collabIndices.map((i) => ({
    src: SESSION_COLLABORATORS[i].avatar,
    name: SESSION_COLLABORATORS[i].name,
  })),
}));

const capitalize = (value) => {
  if (!value || typeof value !== 'string') return '---';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatCollabStatusLabel = (status) => {
  const s = String(status || '')
    .toLowerCase()
    .trim();
  if (s === 'joined' || s === 'active' || s === 'in session') return 'In Session';
  if (s === 'invited' || s === 'pending') return 'Invited';
  if (s === 'left' || s === 'exited') return 'Exited';
  if (s === 'paused' || s === 'break' || s === 'on_break') return 'Paused';
  return status ? capitalize(status) : '';
};

const normalizeSessionStatus = (value) =>
  String(value || '')
    .toLowerCase()
    .trim();

const getSessionActionLabel = (collaborationStatus, sessionStatus) => {
  const status = normalizeSessionStatus(sessionStatus);
  const collabStatus = normalizeSessionStatus(collaborationStatus);

  if (status === 'ended') return 'View Detail';
  if (collabStatus === 'invited') return 'Join Session';
  if (collabStatus === 'joined') return 'Session Joined';
  return 'View Detail';
};

const canJoinSessionFromListing = (session) => {
  const status = normalizeSessionStatus(session?.status);
  const collabStatus = normalizeSessionStatus(session?.currentUserCollaborationStatus);
  return status !== 'ended' && collabStatus === 'invited';
};

export const mapApiSession = (session) => ({
  id: session.id,
  type: session.type ? capitalize(session.type) : '---',
  visibility: session.visibility_type
    ? formatSessionVisibilityLabel(session.visibility_type)
    : '---',
  note: session.working_note || session.title || '---',
  collaborators: (session.collaborators || []).map((collaborator) => {
    const user = collaborator.user || {};
    const name =
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.email ||
      'Unknown';
    const avatar = getUserAvatarUrl(user);
    return {
      id: collaborator.id || user.id,
      src: avatar,
      avatar,
      name,
      status: formatCollabStatusLabel(collaborator.status),
    };
  }),
  logged: formatWorkedHours(session.total_working_hours),
  endAt: session.ended_at ? dayjs(session.ended_at).format('MMM DD, YYYY h:mm A') : '---',
  status: normalizeSessionStatus(session.status),
  currentUserCollaborationStatus: normalizeSessionStatus(
    session.current_user_collaboration_status
  ),
});


/**
 * Reactstrap mounts the dialog in a `position: relative` wrapper. Force that
 * mount node above the floating session player so Finish/Cancel stay clickable.
 */
const useRaiseModalAbovePlayer = isOpen => {
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    const restoredMounts = [];

    const apply = () => {
      document.querySelectorAll('.amok-modal-wrap--above-player').forEach(wrap => {
        const mount = wrap.parentElement;
        if (!mount) return;

        if (!restoredMounts.some(entry => entry.node === mount)) {
          restoredMounts.push({
            node: mount,
            position: mount.style.position,
            inset: mount.style.inset,
            zIndex: mount.style.zIndex,
            pointerEvents: mount.style.pointerEvents,
          });
        }

        mount.style.position = 'fixed';
        mount.style.inset = '0';
        mount.style.zIndex = '20020';
        mount.style.pointerEvents = 'auto';
      });
    };

    apply();
    const frame = window.requestAnimationFrame(apply);
    return () => {
      window.cancelAnimationFrame(frame);
      restoredMounts.forEach(({ node, position, inset, zIndex, pointerEvents }) => {
        node.style.position = position;
        node.style.inset = inset;
        node.style.zIndex = zIndex;
        node.style.pointerEvents = pointerEvents;
      });
    };
  }, [isOpen]);
};

/**
 * ExitSessionModal — Figma 40004585:11326
 */
export const ExitSessionModal = ({
  isOpen,
  onClose,
  project,
  city = null,
  onFinish,
  isSubmitting = false,
}) => {
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  useRaiseModalAbovePlayer(isOpen);

  useEffect(() => {
    if (!isOpen) {
      setNote('');
      setNoteTouched(false);
    }
  }, [isOpen]);

  const noteError = noteTouched ? getWorkingNoteError(note) : undefined;

  const handleClose = () => {
    if (isSubmitting) return
    onClose?.()
  }

  const handleNoteChange = event => {
    setNote(String(event.target.value || '').slice(0, WORKING_NOTE_MAX_LENGTH))
  }

  const handleConfirm = () => {
    if (isSubmitting) return
    setNoteTouched(true)
    if (getWorkingNoteError(note)) return
    onFinish?.({ note: note.trim() })
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      zIndex={20020}
      wrapClassName="amok-modal-wrap--above-player"
      container={typeof document !== 'undefined' ? document.body : undefined}
      className="amok-modal amok-modal--session amok-modal--shadow-session"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body project-session-modal__body">
        <div className="project-session-modal__main">
          <div className="project-session-modal__header">
            <h3 className="project-session-modal__title">
              Are you sure you want to exit this session?
            </h3>
            <p className="project-session-modal__subtitle">Your progress will be saved.</p>
          </div>

          <div className="project-session-modal__fields">
            {city?.id || city?.name ? (
              <SelectedCity city={city} />
            ) : (
              <SelectedProject project={project} />
            )}
            <NoteField
              label="Summary Note"
              value={note}
              onChange={handleNoteChange}
              onBlur={() => setNoteTouched(true)}
              placeholder="Describe what you did..."
              disabled={isSubmitting}
              error={noteError}
            />
          </div>
        </div>

        <ModalActions
          onCancel={handleClose}
          onConfirm={handleConfirm}
          confirmLabel="Finish Session"
          confirmDisabled={Boolean(getWorkingNoteError(note))}
          isSubmitting={isSubmitting}
        />
      </ModalBody>
    </Modal>
  );
};

/**
 * SessionCompletedModal — success after finish/join
 */
export const SessionCompletedModal = ({
  isOpen,
  onClose,
  time = '00:00:00',
  xp = '0',
  redirectHint = 'Redirecting to project detail...',
}) => {
  useRaiseModalAbovePlayer(isOpen);
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(onClose, 2500);
    return () => clearTimeout(t);
  }, [isOpen, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      zIndex={20020}
      wrapClassName="amok-modal-wrap--above-player"
      container={typeof document !== 'undefined' ? document.body : undefined}
      className="amok-modal amok-modal--completed amok-modal--session-completed"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body project-session-completed">
        <div className="project-session-completed__success">
          <div className="amok-modal__check" aria-hidden="true">
            <div className="amok-modal__check-inner">
              <img
                src={ICONS.tickWhite}
                alt=""
                className="amok-modal__check-tick"
              />
            </div>
          </div>
          <div className="project-session-completed__copy">
            <h3 className="project-session-completed__title">Session Completed!</h3>
            <p className="project-session-completed__subtitle">Great work today.</p>
          </div>
        </div>

        <div className="project-session-completed__stats">
          <div className="project-session-completed__stat project-session-completed__stat--orange">
            <span className="project-session-completed__stat-icon">
              <img src={ICONS.timerOrange} alt="" />
            </span>
            <span className="project-session-completed__stat-text">
              <span className="project-session-completed__stat-label">Time Logged</span>
              <span className="project-session-completed__stat-value">{time}</span>
            </span>
          </div>
          {/* <div className="project-session-completed__stat project-session-completed__stat--green">
            <span className="project-session-completed__stat-icon">
              <img src={ICONS.saveAdd} alt="" />
            </span>
            <span className="project-session-completed__stat-text">
              <span className="project-session-completed__stat-label">XP Earned</span>
              <span className="project-session-completed__stat-value">{xp} XP</span>
            </span>
          </div> */}
        </div>

        <p className="project-session-completed__redirect">{redirectHint}</p>
      </ModalBody>
    </Modal>
  );
};

/**
 * ProjectSessions — Figma 40004184:1498 sessions table + modals
 */
const ProjectSessions = ({ project }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { openSessionDetail, isOpen, sessionId, minimize } =
    useSessionDetailPlayer();
  const { sessionModal, setSessionModal, registerTabRefresh } =
    useOutletContext();
  const activeModal = sessionModal;
  const close = () => setSessionModal(null);
  const open = (modal) => setSessionModal(modal);

  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isJoiningSession, setIsJoiningSession] = useState(false);
  const [joiningSession, setJoiningSession] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [sessionType, setSessionType] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_SESSION_SORT_BY);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SESSION_SORT_DIRECTION);

  const currentUser = useSelector((state) => state.auth.currentUser);
  const currentUserType = useSelector(state => state.auth?.currentUser?.type)
  
  const projectCollaborators = useMemo(
    () =>
      getProjectCollaborators(project, currentUser, {
        excludeCurrentUser: true,
      }),
    [project, currentUser]
  );
  const resolvedProjectId = projectId || project?.id;
  const sessionsReturnTo = useMemo(
    () => ({
      returnTo: projectTabReturnTo(
        location.pathname,
        'sessions',
        resolvedProjectId,
      ),
      returnLabel: 'Redirecting to sessions...',
    }),
    [location.pathname, resolvedProjectId],
  );


  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchQuery(searchQuery.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [resolvedProjectId, debouncedSearchQuery, sessionType, sortBy, sortDirection]);

  useEffect(() => {
    if (typeof registerTabRefresh !== 'function') return undefined;
    return registerTabRefresh(() => {
      setListVersion((v) => v + 1);
    });
  }, [registerTabRefresh]);

  useEffect(() => {
    const targetSessionId = location.state?.openJoinSessionId;
    if (!targetSessionId || !resolvedProjectId || isLoading) return undefined;

    let cancelled = false;

    const clearJoinNavigationState = () => {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    };

    const openJoinModalForSession = async () => {
      let session = sessions.find(
        item => String(item.id) === String(targetSessionId),
      );

      if (!session) {
        try {
          const response = await get(endpoints.getSessionById(targetSessionId), {
            params: { 'with[]': ['collaborators.user'] },
            skipErrorToast: true,
          });

          if (response?.data?.success) {
            session = mapApiSession(response.data.result);
          }
        } catch {
          session = null;
        }
      }

      if (cancelled) return;

      if (!session || !canJoinSessionFromListing(session)) {
        clearJoinNavigationState();
        return;
      }

      setJoiningSession(session);
      setSessionModal('join');
      clearJoinNavigationState();
    };

    openJoinModalForSession();

    return () => {
      cancelled = true;
    };
  }, [
    location.state?.openJoinSessionId,
    location.pathname,
    location.search,
    sessions,
    isLoading,
    resolvedProjectId,
    navigate,
    setSessionModal,
  ]);

  // After exit + leave note, keep listing rows in sync without a hard reload.
  useEffect(() => {
    const handleSessionLeft = (event) => {
      const leftSessionId = event?.detail?.sessionId;
      if (!leftSessionId) return;

      const {
        endedAt,
        totalWorkingHours,
        sessionStatus,
        collaborationStatus,
      } = event.detail || {};

      setSessions((prev) =>
        prev.map((item) => {
          if (String(item.id) !== String(leftSessionId)) return item;

          const next = {
            ...item,
            currentUserCollaborationStatus:
              collaborationStatus || 'left',
          };

          if (sessionStatus) {
            next.status = normalizeSessionStatus(sessionStatus);
          }

          if (endedAt) {
            next.endAt = dayjs(endedAt).format('MMM DD, YYYY h:mm A');
          }

          if (
            totalWorkingHours != null &&
            Number.isFinite(Number(totalWorkingHours))
          ) {
            next.logged = formatWorkedHours(totalWorkingHours);
          }

          return next;
        })
      );

      setListVersion((v) => v + 1);
    };

    window.addEventListener('session-left', handleSessionLeft);
    return () => window.removeEventListener('session-left', handleSessionLeft);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchSessions = async () => {
      if (!resolvedProjectId) return;
      setIsLoading(true);
      setError(null);

      try {
        const params = {
          'project_ids[]': resolvedProjectId,
          'with[]': ['collaborators.user'],
          page: currentPage,
          page_size: PAGE_SIZE,
          sort_by: sortBy,
          sort_direction: sortDirection,
        };

        if (debouncedSearchQuery) {
          params.search = debouncedSearchQuery;
        }
        if (sessionType) {
          params.type = sessionType;
        }

        const response = await get(endpoints.getSessions, { params });

        if (cancelled) return;

        if (response?.data?.success) {
          const result = response.data.result || {};
          const rows = (result.data || []).map(mapApiSession);
          const newTotal = result.total ?? rows.length;
          setSessions(rows);
          setTotalItems(newTotal);

          const totalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
          const safePage = Math.min(currentPage, totalPages);
          if (safePage !== currentPage) {
            setCurrentPage(safePage);
          }
        } else {
          setError(response?.data?.message || 'Failed to load sessions');
          setSessions([]);
          setTotalItems(0);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Failed to load sessions');
        setSessions([]);
        setTotalItems(0);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchSessions();
    return () => {
      cancelled = true;
    };
  }, [
    resolvedProjectId,
    currentPage,
    listVersion,
    debouncedSearchQuery,
    sessionType,
    sortBy,
    sortDirection,
  ]);

  const createSession = async ({ type, note, userIds = [] }) => {
    if (!resolvedProjectId || isCreatingSession) return null;

    setIsCreatingSession(true);
    try {
      const result = await createProjectSession({
        type,
        projectId: resolvedProjectId,
        note,
        userIds,
      });

      if (!result.ok) {
        if (result.reason === 'empty_note') {
          toast.error('Please add a working note before starting the session.', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        } else if (result.response) {
          toast.error(result.response?.data?.message || 'Failed to create session', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        }
        return null;
      }

      toast.success(
        type === 'solo' ? 'Solo session created.' : 'Shadow session created.',
        {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        }
      );
      close();
      setListVersion((v) => v + 1);
      if (result.created?.id) {
        openSessionDetail({
          sessionId: result.created.id,
          projectId: resolvedProjectId,
          project,
          ...sessionsReturnTo,
        });
      }
      return result.created;
    } catch (err) {
      if (err?.response || err?.code) {
        return null;
      }
      toast.error(err?.message || 'Failed to create session', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return null;
    } finally {
      setIsCreatingSession(false);
    }
  };

  const startShadow = async ({ note, userIds = [] }) => {
    await createSession({ type: 'shadow', note, userIds });
  };

  const startSolo = async ({ note }) => {
    await createSession({ type: 'solo', note, userIds: [] });
  };

  const finish = () => {
    close();
    open('completed');
  };

  const closeJoinModal = () => {
    if (isJoiningSession) return;
    setJoiningSession(null);
    close();
  };

  const join = async ({ note } = {}) => {
    if (isJoiningSession) return null;

    const sessionIdToJoin = joiningSession?.id;
    if (!sessionIdToJoin) {
      toast.error('No session selected to join.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return null;
    }

    setIsJoiningSession(true);
    try {
      const result = await joinProjectSession({
        sessionId: sessionIdToJoin,
        note,
      });

      if (!result.ok) {
        if (result.reason === 'empty_note') {
          toast.error('Please add a working note before joining the session.', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        } else if (result.response) {
          toast.error(result.response?.data?.message || 'Failed to join session', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        }
        return null;
      }

      toast.success('Joined session.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });

      setSessions((prev) =>
        prev.map((item) =>
          String(item.id) === String(sessionIdToJoin)
            ? { ...item, currentUserCollaborationStatus: 'joined' }
            : item
        )
      );
      setJoiningSession(null);
      close();
      openSessionDetail({
        sessionId: sessionIdToJoin,
        projectId: resolvedProjectId,
        project,
        ...sessionsReturnTo,
      });
      return result.joined;
    } catch (err) {
      if (err?.response || err?.code) {
        return null;
      }
      toast.error(err?.message || 'Failed to join session', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return null;
    } finally {
      setIsJoiningSession(false);
    }
  };

  const onRowAction = (session) => {
    if (!session?.id) return;

    if (canJoinSessionFromListing(session)) {
      const activeSessionId = getActiveSessionSnapshot().sessionId;
      if (activeSessionId && String(session.id) !== String(activeSessionId)) {
        toast.error(
          "You’re already in a session. Leave your current session to join another.",
          {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          }
        );
        return;
      }
      setJoiningSession(session);
      open('join');
      return;
    }

    const floatingSessionId = sessionId ? String(sessionId) : '';
    const clickedId = String(session.id);
    if (isOpen && floatingSessionId && clickedId !== floatingSessionId) {
      minimize?.();
      const link = currentUserType === "member" ? `/projects/${resolvedProjectId}/sessions/${clickedId}` : `/admin/projects/${resolvedProjectId}/sessions/${clickedId}`;
      navigate(link);
      return;
    }

    openSessionDetail({
      sessionId: session.id,
      projectId: resolvedProjectId,
      project,
      ...sessionsReturnTo,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSessionId || isDeleting) return;
    setIsDeleting(true);
    try {
      const response = await del(endpoints.deleteSession(deletingSessionId));
      if (!response || response?.data?.success === false) {
        if (response) {
          toast.error(response?.data?.message || 'Failed to delete session', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        }
        return;
      }

      toast.success('Session deleted successfully.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });

      const remainingOnPage = sessions.filter(
        (session) => session.id !== deletingSessionId
      ).length;
      const newTotal = Math.max(0, totalItems - 1);
      const newTotalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));

      if (currentPage > newTotalPages) {
        setCurrentPage(newTotalPages);
      } else if (remainingOnPage === 0 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        setListVersion((version) => version + 1);
      }
    } catch (err) {
      if (err?.response || err?.code) {
        return;
      }
      toast.error(err?.message || 'Failed to delete session', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeletingSessionId(null);
    }
  };

  return (
    <div className="project-sessions">
      <div className="project-sessions__controls">
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search sessions..."
          iconSrc={ICONS.search}
        />
        <FilterDropdown
          filters={[
            {
              key: 'type',
              type: 'select',
              label: 'Type',
              multiple: false,
              value: sessionType,
              defaultValue: '',
              onChange: setSessionType,
              options: SESSION_TYPE_OPTIONS,
            },
            {
              key: 'sortBy',
              type: 'sortBy',
              label: 'Sort By',
              value: sortBy,
              defaultValue: DEFAULT_SESSION_SORT_BY,
              onChange: setSortBy,
              options: SESSION_SORT_OPTIONS,
            },
            {
              key: 'sortDirection',
              type: 'sortDirection',
              label: 'Order',
              value: sortDirection,
              defaultValue: DEFAULT_SESSION_SORT_DIRECTION,
              onChange: setSortDirection,
            },
          ]}
        />
      </div>

      <div className="project-sessions__table-wrap">
        <div
          className="project-sessions__table"
          role="table"
          aria-label="Project sessions"
          aria-busy={isLoading || undefined}
        >
          <div className="project-sessions__head" role="row">
            <div className="project-sessions__cell project-sessions__cell--type" role="columnheader">
              Session Type
            </div>
            <div
              className="project-sessions__cell project-sessions__cell--visibility"
              role="columnheader"
            >
              Visibility
            </div>
            <div className="project-sessions__cell project-sessions__cell--note" role="columnheader">
              Working Note
            </div>
            <div
              className="project-sessions__cell project-sessions__cell--collab"
              role="columnheader"
            >
              Collaborators
            </div>
            <div
              className="project-sessions__cell project-sessions__cell--hours"
              role="columnheader"
            >
              Logged Hours
            </div>
            <div className="project-sessions__cell project-sessions__cell--end" role="columnheader">
              Session End At
            </div>
            <div
              className="project-sessions__cell project-sessions__cell--action"
              role="columnheader"
            >
              Action
            </div>
          </div>

          {isLoading &&
            Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="project-sessions__row project-sessions__row--skeleton"
                role="row"
                aria-hidden="true"
              >
                {Array.from({ length: TABLE_COLUMNS }).map((__, cellIdx) => (
                  <div key={cellIdx} className="project-sessions__skeleton-cell">
                    <div className="project-sessions__skeleton-bar" />
                  </div>
                ))}
              </div>
            ))}

          {!isLoading &&
            sessions.map((session) => (
              <div key={session.id} className="project-sessions__row" role="row">
                <div
                  className="project-sessions__cell project-sessions__cell--type"
                  role="cell"
                  data-label="Session Type"
                >
                  <span className="project-sessions__type">
                    <img
                      src={
                        /solo/i.test(session.type)
                          ? ICONS.soloSession
                          : ICONS.session
                      }
                      alt=""
                    />
                    {session.type}
                  </span>
                </div>
                <div
                  className="project-sessions__cell project-sessions__cell--visibility"
                  role="cell"
                  data-label="Visibility"
                >
                  <span
                    className={`project-sessions__visibility project-sessions__visibility--${
                      !session.visibility || session.visibility === '---'
                        ? 'none'
                        : getSessionVisibilityClassKey(session.visibility)
                    }`}
                  >
                    {session.visibility === '---' ? '---' : session.visibility}
                  </span>
                </div>
                <div
                  className="project-sessions__cell project-sessions__cell--note"
                  role="cell"
                  data-label="Working Note"
                >
                  <TruncatedText
                    className="project-sessions__note"
                    title={session.note}
                  >
                    {session.note}
                  </TruncatedText>
                </div>
                <div
                  className="project-sessions__cell project-sessions__cell--collab"
                  role="cell"
                  data-label="Collaborators"
                >
                  <AvatarGroup people={session.collaborators} size="xs" max={4} />
                </div>
                <div
                  className="project-sessions__cell project-sessions__cell--hours"
                  role="cell"
                  data-label="Logged Hours"
                >
                  {session.logged}
                </div>
                <div
                  className="project-sessions__cell project-sessions__cell--end"
                  role="cell"
                  data-label="Session End At"
                >
                  {session.endAt}
                </div>
                <div
                  className="project-sessions__cell project-sessions__cell--action"
                  role="cell"
                  data-label="Action"
                >
                  {currentUserType === "member" ? (
                    <>
                      <CustomButton
                        variant="secondary"
                        size="sm"
                        trailingIconSrc={ICONS.arrowRightBlack}
                        trailingIconAlt=""
                        onClick={() => onRowAction(session)}
                      >
                        {getSessionActionLabel(
                          session.currentUserCollaborationStatus,
                          session.status
                        )}
                      </CustomButton>
                      <CustomIconButton
                        iconSrc={ICONS.edit}
                        label="Edit"
                        size="sm"
                        onClick={() => onRowAction(session)}
                      />
                      {project?.is_current_user_active_member ? (
                        <CustomIconButton
                          iconSrc={ICONS.trash}
                          label="Delete"
                          tone="danger"
                          size="sm"
                          onClick={() => {
                            setDeletingSessionId(session.id);
                            setDeleteModalOpen(true);
                          }}
                        />
                      ) : null}
                    </>
                  ) : (
                      <>
                        {project.is_current_user_active_member ? (
                          <CustomButton
                            variant="secondary"
                            size="sm"
                            trailingIconSrc={ICONS.arrowRightBlack}
                            trailingIconAlt=""
                            onClick={() => onRowAction(session)}
                          >
                            {getSessionActionLabel(
                              session.currentUserCollaborationStatus,
                              session.status
                            )}
                          </CustomButton>
                        ) :

                          <CustomButton
                            variant="secondary"
                            size="sm"
                            trailingIconSrc={ICONS.arrowRightBlack}
                            trailingIconAlt=""
                            onClick={() =>
                              openSessionDetail({
                                sessionId: session.id,
                                projectId: resolvedProjectId,
                                project,
                                ...sessionsReturnTo,
                              })
                            }
                          >
                            View Detail
                          </CustomButton>

                        }
                      {project?.is_current_user_active_member ? (
                        <CustomIconButton
                          iconSrc={ICONS.trash}
                          label="Delete"
                          tone="danger"
                          size="sm"
                          onClick={() => {
                            setDeletingSessionId(session.id);
                            setDeleteModalOpen(true);
                          }}
                        />
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            ))}

          {!isLoading && error && <div className="project-sessions__error">{error}</div>}

          {!isLoading && !error && sessions.length === 0 && (
            <EmptyState
              title="No Sessions Yet"
              description={
                project?.is_current_user_active_member
                  ? "Start a Shadow or Solo session to begin logging collaborative work on this project."
                  : "No sessions have been logged on this project yet."
              }
              actionText={
                project?.is_current_user_active_member
                  ? "Start Shadow Session"
                  : undefined
              }
              onActionClick={
                project?.is_current_user_active_member
                  ? () => open("shadow")
                  : undefined
              }
            />
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
        itemLabel="Sessions"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
      />

      <DeleteConfirmation
        isOpen={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeletingSessionId(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Session"
        message="Are you sure you want to delete this session? This action cannot be undone."
        cancelText="Cancel"
        confirmText="Delete Session"
        isDeleting={isDeleting}
      />

      <StartShadowSessionModal
        isOpen={activeModal === 'shadow'}
        onClose={close}
        project={project}
        collaborators={projectCollaborators}
        onStart={startShadow}
        isSubmitting={isCreatingSession}
      />

      <StartSoloSessionModal
        isOpen={activeModal === 'solo'}
        onClose={close}
        project={project}
        onStart={startSolo}
        isSubmitting={isCreatingSession}
      />

      <JoinShadowSessionModal
        isOpen={activeModal === 'join'}
        onClose={closeJoinModal}
        project={project}
        collaborators={(joiningSession?.collaborators || []).filter(
          (person) => person.status !== 'Exited'
        )}
        onJoin={join}
        isSubmitting={isJoiningSession}
      />

      <ExitSessionModal
        isOpen={activeModal === 'exit'}
        onClose={close}
        project={project}
        onFinish={finish}
      />

      <SessionCompletedModal
        isOpen={activeModal === 'completed'}
        onClose={close}
        time="00:50:24"
        xp="120"
      />
    </div>
  );
};

export default ProjectSessions;
